import { ShapeGeometry } from 'three'

type CachedSvgFillGeometries = {
  geometries: ShapeGeometry[][]
  refCount: number
}

const cache = new Map<string, CachedSvgFillGeometries>()

export const buildSvgFillGeometryCacheKey = (
  src: string,
  options: {
    skipFill: boolean
    flipped: boolean
    flipHorizontal: boolean
    flipVertical: boolean
  },
): string =>
  [
    options.skipFill ? '1' : '0',
    options.flipped ? '1' : '0',
    options.flipHorizontal ? '1' : '0',
    options.flipVertical ? '1' : '0',
    src,
  ].join('|')

/**
 * Share tessellated SVG fill geometry across instances with the same source
 * and flip flags. 34 identical product stools should tessellate once.
 */
export const acquireSvgFillGeometries = (
  key: string,
  factory: () => ShapeGeometry[][],
): ShapeGeometry[][] => {
  const existing = cache.get(key)
  if (existing) {
    existing.refCount += 1
    return existing.geometries
  }

  const geometries = factory()
  cache.set(key, { geometries, refCount: 1 })
  return geometries
}

export const releaseSvgFillGeometries = (key: string): void => {
  const existing = cache.get(key)
  if (!existing) {
    return
  }

  existing.refCount -= 1
  if (existing.refCount > 0) {
    return
  }

  existing.geometries.forEach((pathGeometries) => {
    pathGeometries?.forEach((geometry) => geometry.dispose())
  })
  cache.delete(key)
}

export const clearSvgFillGeometryCacheForTests = (): void => {
  cache.forEach((entry) => {
    entry.geometries.forEach((pathGeometries) => {
      pathGeometries?.forEach((geometry) => geometry.dispose())
    })
  })
  cache.clear()
}

export const getSvgFillGeometryCacheSizeForTests = (): number => cache.size
